/**
 * ErrorBoundary — catches runtime errors in the React tree.
 *
 * Healthcare requirement: the app must NEVER show a raw white screen or
 * uncaught JS error to a clinician. This boundary provides a graceful fallback
 * with a "Reload" action and error details for support.
 *
 * Must be a class component — React does not support error boundaries as
 * function components (no hooks equivalent for componentDidCatch).
 */
import React from 'react';

interface Props {
  children: React.ReactNode;
  /** Optional: EHR theme for branded error screen */
  theme?: 'cerner' | 'ecw' | 'generic' | 'openemr';
}

interface State {
  hasError: boolean;
  errorMessage: string;
  errorId: string;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: '', errorId: '' };
  }

  static getDerivedStateFromError(error: unknown): State {
    const errorId = `ERR-${Date.now().toString(36).toUpperCase()}`;
    const message =
      error instanceof Error
        ? error.message
        : 'An unexpected error occurred.';
    return { hasError: true, errorMessage: message, errorId };
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo) {
    // In production this would send to an error tracking service (e.g. Sentry).
    // Do NOT log PHI — log only the error structure.
    console.error('[RAAPID ErrorBoundary]', {
      errorId: this.state.errorId,
      error: error instanceof Error ? error.message : String(error),
      componentStack: info.componentStack,
    });
  }

  handleReload = () => {
    // Clear error state and re-render children
    this.setState({ hasError: false, errorMessage: '', errorId: '' });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const accentColor =
      this.props.theme === 'ecw' ? '#00897B' : '#0079C1';

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          padding: '24px',
          background: '#f9fafb',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          textAlign: 'center',
        }}
      >
        {/* Brand bar */}
        <div
          style={{
            width: '48px',
            height: '4px',
            borderRadius: '2px',
            background: accentColor,
            marginBottom: '20px',
          }}
        />

        {/* Icon */}
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            background: '#fef2f2',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '16px',
          }}
        >
          <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#dc2626" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>

        <h2
          style={{ fontSize: '15px', fontWeight: 700, color: '#111827', margin: '0 0 8px' }}
        >
          RAAPID encountered an error
        </h2>
        <p style={{ fontSize: '12px', color: '#6b7280', maxWidth: '280px', margin: '0 0 20px', lineHeight: '1.5' }}>
          The application ran into an unexpected problem. Your EHR data is unaffected.
          {' '}Please reload or contact support.
        </p>

        {/* Error reference */}
        <div
          style={{
            background: '#f3f4f6',
            border: '1px solid #e5e7eb',
            borderRadius: '6px',
            padding: '8px 12px',
            marginBottom: '20px',
            fontSize: '10px',
            fontFamily: 'monospace',
            color: '#6b7280',
          }}
        >
          Reference: {this.state.errorId}
        </div>

        <button
          onClick={this.handleReload}
          style={{
            padding: '10px 24px',
            background: accentColor,
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Reload RAAPID
        </button>
      </div>
    );
  }
}
